import React, { useState, useEffect } from 'react';
import { Card, Badge, Alert, Form, Row, Col, Button, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faFileAlt,
  faLightbulb,
  faChevronDown,
  faChevronUp,
  faGavel,
  faClock,
  faDollarSign,
  faRobot,
  faSignature,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import './SmartDocumentChecklist.css';

interface SmartDocumentChecklistProps {
  propertyId?: string;
  transactionId?: string;
  onDocumentSelect?: (documentId: string) => void;
}

interface PropertyInfo {
  year_built: number;
  has_hoa: boolean;
  has_pool: boolean;
  has_spa: boolean;
  water_source: string;
  septic_system: boolean;
  flood_zone: string;
  requires_flood_insurance: boolean;
  type: string;
  has_solar: boolean;
  solar_ownership?: string;
}

interface TransactionInfo {
  loan_type: string;
  transaction_type: string;
}

interface DocumentRequirement {
  id: string;
  name: string;
  required: boolean | string;
  reason?: string;
  timing?: string;
  legal_basis?: string;
  penalties?: string;
  conditions?: any[];
  exemptions?: string[];
  includes?: string[];
  template_id?: string;
  source?: string;
  // Field mapping status (KOPS-207 enhancement)
  fieldMappingStatus?: {
    hasFields: boolean;
    fieldCount?: number;
    signatureCount?: number;
    confidence?: number;
    detectionMethod?: string;
  };
}

interface ChecklistResult {
  required: DocumentRequirement[];
  conditional: DocumentRequirement[];
  not_required: DocumentRequirement[];
  total_required: number;
}

const SmartDocumentChecklist: React.FC<SmartDocumentChecklistProps> = ({
  propertyId,
  transactionId,
  onDocumentSelect
}) => {
  const [propertyInfo, setPropertyInfo] = useState<PropertyInfo>({
    year_built: 2000,
    has_hoa: false,
    has_pool: false,
    has_spa: false,
    water_source: 'city',
    septic_system: false,
    flood_zone: 'X',
    requires_flood_insurance: false,
    type: 'single_family',
    has_solar: false
  });

  const [transactionInfo, setTransactionInfo] = useState<TransactionInfo>({
    loan_type: 'conventional',
    transaction_type: 'purchase'
  });

  const [checklist, setChecklist] = useState<ChecklistResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState<{ [key: string]: boolean }>({});
  const [manualMode, setManualMode] = useState(!propertyId);

  useEffect(() => {
    if (propertyId && transactionId) {
      fetchPropertyAndTransactionData();
    } else if (!propertyId) {
      // In manual mode, generate checklist immediately
      generateChecklist();
    }
  }, [propertyId, transactionId]);

  const fetchPropertyAndTransactionData = async () => {
    try {
      setLoading(true);
      const [propertyRes, transactionRes] = await Promise.all([
        axiosInstance.get(`/properties/${propertyId}`),
        axiosInstance.get(`/transactions/${transactionId}`)
      ]);

      setPropertyInfo(propertyRes.data);
      setTransactionInfo(transactionRes.data);
      
      // Generate checklist with fetched data
      generateChecklist(propertyRes.data, transactionRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setManualMode(true);
    } finally {
      setLoading(false);
    }
  };

  const generateChecklist = async (propInfo?: PropertyInfo, transInfo?: TransactionInfo) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('/documents/smart-checklist', {
        property_info: propInfo || propertyInfo,
        transaction_info: transInfo || transactionInfo
      });

      setChecklist(response.data);
    } catch (error) {
      console.error('Error generating checklist:', error);
      // Fallback to client-side generation
      generateClientSideChecklist(propInfo || propertyInfo, transInfo || transactionInfo);
    } finally {
      setLoading(false);
    }
  };

  const generateClientSideChecklist = (propInfo: PropertyInfo, transInfo: TransactionInfo) => {
    // This would use the ConditionalDocumentEvaluator logic
    // For now, we'll create a sample response
    const mockChecklist: ChecklistResult = {
      required: [
        {
          id: 'purchase_agreement',
          name: 'Purchase Agreement',
          required: true,
          timing: 'At offer submission',
          reason: 'Always required for this transaction type'
        },
        {
          id: 'property_disclosure',
          name: 'Property Disclosure Statement',
          required: true,
          timing: 'With initial offer or within 3 days',
          reason: 'Always required for this transaction type'
        }
      ],
      conditional: [],
      not_required: [],
      total_required: 2
    };

    // Add conditional documents based on property info
    if (propInfo.year_built < 1978 && propInfo.type === 'single_family') {
      mockChecklist.conditional.push({
        id: 'lead_paint_disclosure',
        name: 'Lead Paint Disclosure',
        required: 'conditional',
        timing: 'Before buyer is obligated under contract',
        reason: 'Required for residential properties built before 1978',
        legal_basis: 'Title X - Residential Lead-Based Paint Hazard Reduction Act',
        penalties: 'Up to $16,000 per violation'
      });
    }

    if (propInfo.has_hoa) {
      mockChecklist.conditional.push({
        id: 'hoa_disclosure',
        name: 'HOA Disclosure & Documents',
        required: 'conditional',
        timing: 'With initial disclosure package',
        reason: 'Required when property is part of HOA',
        includes: ['HOA bylaws', 'Financial statements', 'Meeting minutes']
      });
    }

    if (transInfo.loan_type === 'FHA') {
      mockChecklist.conditional.push({
        id: 'fha_amendatory',
        name: 'FHA Amendatory Clause',
        required: 'conditional',
        timing: 'Before signing purchase agreement',
        reason: 'Required for all FHA-insured loans'
      });
    }

    mockChecklist.total_required = mockChecklist.required.length + mockChecklist.conditional.length;
    setChecklist(mockChecklist);
  };

  const handlePropertyChange = (field: keyof PropertyInfo, value: any) => {
    const newPropertyInfo = { ...propertyInfo, [field]: value };
    setPropertyInfo(newPropertyInfo);
    
    // Regenerate checklist with new info
    generateChecklist(newPropertyInfo, transactionInfo);
  };

  const handleTransactionChange = (field: keyof TransactionInfo, value: any) => {
    const newTransactionInfo = { ...transactionInfo, [field]: value };
    setTransactionInfo(newTransactionInfo);
    
    // Regenerate checklist with new info
    generateChecklist(propertyInfo, newTransactionInfo);
  };

  const toggleDetails = (docId: string) => {
    setShowDetails(prev => ({ ...prev, [docId]: !prev[docId] }));
  };

  const getDocumentIcon = (doc: DocumentRequirement) => {
    if (doc.legal_basis) return faGavel;
    if (doc.penalties) return faDollarSign;
    return faFileAlt;
  };

  const getDocumentBadgeColor = (doc: DocumentRequirement) => {
    if (doc.required === true) return 'danger';
    if (doc.required === 'conditional') return 'warning';
    return 'secondary';
  };

  return (
    <div className="smart-document-checklist">
      {manualMode && (
        <Card className="mb-4 border-0" style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px'
        }}>
          <Card.Header style={{
            background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%)',
            borderBottom: '1px solid rgba(0, 212, 255, 0.2)'
          }}>
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faLightbulb} className="me-2" style={{ color: '#00d4ff' }} />
              Property & Transaction Details
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <h6 className="text-muted mb-3">Property Information</h6>
                <Form.Group className="mb-3">
                  <Form.Label>Year Built</Form.Label>
                  <Form.Control
                    type="number"
                    value={propertyInfo.year_built}
                    onChange={(e) => handlePropertyChange('year_built', parseInt(e.target.value))}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Property Type</Form.Label>
                  <Form.Select
                    value={propertyInfo.type}
                    onChange={(e) => handlePropertyChange('type', e.target.value)}
                  >
                    <option value="single_family">Single Family</option>
                    <option value="condo">Condo</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="multi_family">Multi-Family</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Has HOA"
                    checked={propertyInfo.has_hoa}
                    onChange={(e) => handlePropertyChange('has_hoa', e.target.checked)}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Has Pool"
                    checked={propertyInfo.has_pool}
                    onChange={(e) => handlePropertyChange('has_pool', e.target.checked)}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Has Septic System"
                    checked={propertyInfo.septic_system}
                    onChange={(e) => handlePropertyChange('septic_system', e.target.checked)}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <h6 className="text-muted mb-3">Transaction Information</h6>
                <Form.Group className="mb-3">
                  <Form.Label>Loan Type</Form.Label>
                  <Form.Select
                    value={transactionInfo.loan_type}
                    onChange={(e) => handleTransactionChange('loan_type', e.target.value)}
                  >
                    <option value="conventional">Conventional</option>
                    <option value="FHA">FHA</option>
                    <option value="VA">VA</option>
                    <option value="cash">Cash</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Water Source</Form.Label>
                  <Form.Select
                    value={propertyInfo.water_source}
                    onChange={(e) => handlePropertyChange('water_source', e.target.value)}
                  >
                    <option value="city">City Water</option>
                    <option value="well">Well Water</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Flood Zone</Form.Label>
                  <Form.Select
                    value={propertyInfo.flood_zone}
                    onChange={(e) => handlePropertyChange('flood_zone', e.target.value)}
                  >
                    <option value="X">Zone X (Low Risk)</option>
                    <option value="A">Zone A (High Risk)</option>
                    <option value="AE">Zone AE (High Risk)</option>
                    <option value="V">Zone V (Coastal High Risk)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {checklist && (
        <div>
          <Alert variant="info" className="mb-4" style={{
            background: 'rgba(0, 212, 255, 0.1)',
            border: '1px solid rgba(0, 212, 255, 0.3)'
          }}>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            <strong>{checklist.total_required} documents</strong> required for this transaction
          </Alert>

          {/* Required Documents */}
          {checklist.required.length > 0 && (
            <Card className="mb-4 border-0" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px'
            }}>
              <Card.Header style={{
                background: 'rgba(239, 68, 68, 0.1)',
                borderBottom: '1px solid rgba(239, 68, 68, 0.2)'
              }}>
                <h6 className="mb-0">
                  <Badge bg="danger" className="me-2">{checklist.required.length}</Badge>
                  Always Required Documents
                </h6>
              </Card.Header>
              <Card.Body>
                {checklist.required.map((doc) => (
                  <div key={doc.id} className="document-item mb-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-1">
                          <FontAwesomeIcon 
                            icon={getDocumentIcon(doc)} 
                            className="me-2" 
                            style={{ color: '#ef4444' }}
                          />
                          <h6 className="mb-0">{doc.name}</h6>
                        </div>
                        {doc.timing && (
                          <div className="text-muted small">
                            <FontAwesomeIcon icon={faClock} className="me-1" />
                            {doc.timing}
                          </div>
                        )}
                        <div className="text-muted small mt-1">{doc.reason}</div>
                        
                        {/* Field Mapping Status - KOPS-207 Enhancement */}
                        {doc.fieldMappingStatus && (
                          <div className="field-mapping-status mt-2">
                            {doc.fieldMappingStatus.hasFields ? (
                              <div className="d-flex align-items-center flex-wrap">
                                <Badge bg="success" className="me-2 mb-1">
                                  <FontAwesomeIcon icon={faRobot} className="me-1" />
                                  {doc.fieldMappingStatus.fieldCount} Fields Mapped
                                </Badge>
                                {doc.fieldMappingStatus.signatureCount && doc.fieldMappingStatus.signatureCount > 0 && (
                                  <Badge bg="primary" className="me-2 mb-1">
                                    <FontAwesomeIcon icon={faSignature} className="me-1" />
                                    {doc.fieldMappingStatus.signatureCount} Signatures
                                  </Badge>
                                )}
                                {doc.fieldMappingStatus.confidence && (
                                  <Badge 
                                    bg={doc.fieldMappingStatus.confidence > 0.8 ? 'success' : 
                                        doc.fieldMappingStatus.confidence > 0.6 ? 'warning' : 'secondary'} 
                                    className="me-2 mb-1"
                                  >
                                    {Math.round(doc.fieldMappingStatus.confidence * 100)}% Confidence
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <Badge bg="secondary" className="me-2">
                                <FontAwesomeIcon icon={faEdit} className="me-1" />
                                Manual Field Mapping Required
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="d-flex flex-column">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => onDocumentSelect && onDocumentSelect(doc.id)}
                          className="mb-1"
                        >
                          Select
                        </Button>
                        {doc.fieldMappingStatus?.hasFields && (
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() => onDocumentSelect && onDocumentSelect(doc.id)}
                          >
                            <FontAwesomeIcon icon={faSignature} className="me-1" />
                            Ready to Fill
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          )}

          {/* Conditional Documents */}
          {checklist.conditional.length > 0 && (
            <Card className="mb-4 border-0" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px'
            }}>
              <Card.Header style={{
                background: 'rgba(245, 158, 11, 0.1)',
                borderBottom: '1px solid rgba(245, 158, 11, 0.2)'
              }}>
                <h6 className="mb-0">
                  <Badge bg="warning" text="dark" className="me-2">
                    {checklist.conditional.length}
                  </Badge>
                  Conditionally Required Documents
                </h6>
              </Card.Header>
              <Card.Body>
                {checklist.conditional.map((doc) => (
                  <div key={doc.id} className="document-item mb-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-1">
                          <FontAwesomeIcon 
                            icon={getDocumentIcon(doc)} 
                            className="me-2" 
                            style={{ color: '#f59e0b' }}
                          />
                          <h6 className="mb-0">{doc.name}</h6>
                          <Button
                            size="sm"
                            variant="link"
                            className="ms-2 p-0"
                            onClick={() => toggleDetails(doc.id)}
                          >
                            <FontAwesomeIcon 
                              icon={showDetails[doc.id] ? faChevronUp : faChevronDown}
                            />
                          </Button>
                        </div>
                        
                        {doc.timing && (
                          <div className="text-muted small">
                            <FontAwesomeIcon icon={faClock} className="me-1" />
                            {doc.timing}
                          </div>
                        )}
                        
                        <div className="text-muted small mt-1">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                          {doc.reason}
                        </div>

                        <Collapse in={showDetails[doc.id]}>
                          <div className="mt-2 p-3" style={{
                            background: 'rgba(0, 0, 0, 0.05)',
                            borderRadius: '8px'
                          }}>
                            {doc.legal_basis && (
                              <div className="mb-2">
                                <strong>Legal Basis:</strong> {doc.legal_basis}
                              </div>
                            )}
                            {doc.penalties && (
                              <div className="mb-2 text-danger">
                                <strong>Penalties:</strong> {doc.penalties}
                              </div>
                            )}
                            {doc.includes && (
                              <div className="mb-2">
                                <strong>Includes:</strong>
                                <ul className="mb-0 mt-1">
                                  {doc.includes.map((item, idx) => (
                                    <li key={idx}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {doc.exemptions && (
                              <div>
                                <strong>Exemptions:</strong>
                                <ul className="mb-0 mt-1">
                                  {doc.exemptions.map((item, idx) => (
                                    <li key={idx}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </Collapse>
                      </div>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => onDocumentSelect && onDocumentSelect(doc.id)}
                      >
                        Select
                      </Button>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          )}

          {/* Not Required Documents (Optional Display) */}
          {checklist.not_required.length > 0 && (
            <Card className="border-0" style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '12px'
            }}>
              <Card.Header style={{
                background: 'transparent',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h6 className="mb-0 text-muted">
                  <Badge bg="secondary" className="me-2">
                    {checklist.not_required.length}
                  </Badge>
                  Not Required (Based on Current Info)
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="text-muted small">
                  {checklist.not_required.map((doc, idx) => (
                    <span key={doc.id}>
                      {doc.name}
                      {idx < checklist.not_required.length - 1 && ', '}
                    </span>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default SmartDocumentChecklist;